import { useState } from "react";
import { useMenuTriggerState } from "react-stately";

import { useMultiSelectListState } from "../../hooks/useMultiSelectListState";

import type {
  AsyncLoadable,
  CollectionBase,
  DOMProps,
  FocusableProps,
  HelpTextProps,
  InputBase,
  Key,
  LabelableProps,
  MultipleSelection,
  TextInputBase,
  Validation,
} from "@react-types/shared";
import type { MenuTriggerState, OverlayTriggerProps } from "react-stately";
import type {
  MultiSelectListState,
  OptionalSingleSelectProps,
} from "../../hooks/useMultiSelectListState";

export interface MultiSelectProps<T>
  extends CollectionBase<T>,
    AsyncLoadable,
    Omit<InputBase, "isReadOnly">,
    DOMProps,
    HelpTextProps,
    Validation<T>,
    LabelableProps,
    TextInputBase,
    Omit<MultipleSelection, "none" | "onSelectionChange">,
    OptionalSingleSelectProps,
    FocusableProps,
    OverlayTriggerProps {
  /**
   * Whether the menu should automatically flip direction when space is limited.
   * @default true
   */
  shouldFlip?: boolean;
  /**
   * Callback invoked when the selection changes.
   */
  onSelectionChange?: (keys: Set<Key>) => any;
}

export interface MultiSelectState<T> extends MultiSelectListState<T>, MenuTriggerState {
  /** Whether the select is currently focused. */
  isFocused: boolean;

  /** Sets whether the select is focused. */
  setFocused(isFocused: boolean): void;
}

export function useMultiSelectState<T extends object>(
  props: MultiSelectProps<T>
): MultiSelectState<T> {
  const [isFocused, setFocused] = useState(false);

  const triggerState = useMenuTriggerState(props);
  const listState = useMultiSelectListState({
    ...props,
    onSelectionChange: keys => {
      if (props.onSelectionChange != null) {
        if (keys === "all") {
          // This may change back to "all" once we will implement async loading of additional
          // items and differentiation between "select all" vs. "select visible".
          props.onSelectionChange(new Set(listState.collection.getKeys()));
        } else {
          props.onSelectionChange(keys);
        }
      }

      // Multi select stays open after item selection
      if (props.selectionMode === "single") {
        triggerState.close();
      }
    },
  });

  return {
    ...listState,
    ...triggerState,
    close() {
      triggerState.close();
    },
    open() {
      // Don't open if the collection is empty.
      if (listState.collection.size !== 0) {
        triggerState.open();
      }
    },
    toggle(focusStrategy) {
      if (listState.collection.size !== 0) {
        triggerState.toggle(focusStrategy);
      }
    },
    isFocused,
    setFocused,
  };
}
